<script setup lang="ts">
import * as Sentry from '@sentry/vue';
import type { NuxtError } from '#app';

const props = defineProps<{
  error: NuxtError;
}>();

const sentryEventId = computed(() => {
  if (import.meta.client) {
    return Sentry.lastEventId();
  }
  return undefined;
});

const errorConfig = computed(() => {
  const status = props.error.statusCode ?? 500;

  if (status === 404) {
    return {
      icon: 'mdi-map-marker-question-outline',
      title: 'Page Not Found',
      message:
        'The page you\u2019re looking for doesn\u2019t exist or has been moved.',
    };
  }
  if (status === 403) {
    return {
      icon: 'mdi-lock-outline',
      title: 'Access Denied',
      message: 'You don\u2019t have permission to view this page.',
    };
  }
  if (status === 410) {
    return {
      icon: 'mdi-link-off',
      title: 'Link Expired',
      message: 'This link has expired and is no longer available.',
    };
  }
  if (status === 409) {
    return {
      icon: 'mdi-sync-alert',
      title: 'Content Changed',
      message: 'This content has been updated. Please refresh and try again.',
    };
  }

  return {
    icon: 'mdi-alert-circle-outline',
    title: 'Something Went Wrong',
    message: 'An unexpected error occurred. Please try again later.',
  };
});

useHead({
  title: () => `${errorConfig.value.title} | Arvee Events`,
  meta: [{ name: 'robots', content: 'noindex, nofollow' }],
});

function handleClearError() {
  clearError({ redirect: '/browse' });
}
</script>

<template>
  <v-app>
    <v-container class="fill-height" fluid>
      <v-row align="center" justify="center">
        <v-col cols="12" sm="8" md="6" lg="4" class="text-center">
          <v-icon
            :icon="errorConfig.icon"
            size="80"
            color="primary"
            class="mb-4"
          />
          <h1 class="text-h4 font-weight-bold mb-2">
            {{ errorConfig.title }}
          </h1>
          <p class="text-body-1 text-medium-emphasis mb-6">
            {{ errorConfig.message }}
          </p>
          <v-btn color="primary" size="large" @click="handleClearError">
            Go Home
          </v-btn>
          <p
            v-if="sentryEventId"
            class="text-caption text-medium-emphasis mt-6"
          >
            Reference: {{ sentryEventId }}
          </p>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>
